import Rx from 'rx'

class ProdutoList {
    static get $inject() {
        return ['$scope', '$uibModal', 'gql', '$http', 'Produtos', 'Etiquetas']
    }

    static get produtoFields() {
        return `    
            id
            codigo
            descricao
            codigoBarras
            ncm
            exTipi
            cest
            ativo
            tipoDestinacao {
                codigo
                descricao
            }
            foto {
                fileName
            }
            reclassificacao {
                id
                problemas
                status
                conclusoes
            }
            codigosFornecedores {
                id
                codigo
                cnpjFornecedor 
                codigoFornecedor
            }
            etiquetas {
                id
                nome
                descricao
                cor
            }
        `
    }

    constructor(scope, modal, gql, $http, Produtos, Etiquetas) {
        this.apply = (fn) => scope.$apply(fn)
        this.emit = (event, message) => scope.$emit(event, message)
        this.broadcast = (event, message) => scope.$broadcast(event, message)
        this.on = (...args) => scope.$on(...args)
        this.modal = modal
        this.gql = gql
        this.http = $http
        this.Produtos = Produtos
        this.Etiquetas = Etiquetas

        this.perPage = 20
        this.produtos = []
        this.estatisticasSubject = new Rx.Subject()
        this.estatisticasObservable = this.estatisticasSubject.debounce(5000)

        this.filterSubject = new Rx.Subject()

        this.cacheKey = 'ef-produto-list-estatistica'
        this.fieldsReprovados = [
            'REPROVADO',
            'INVALID_CODIGO_BARRAS',
            'MISSING_CODIGO_BARRAS',
            'PARAMETROS_INCONSISTENTES',
            'NCM_INVALIDA',
            'CEST_INVALIDO',
            'EX_TIPI_INVALIDA'
        ]
    }

    $onInit() {
        this.etiquetasSubscription = this.subscribeToEtiquetas(this.empresaId)
    }

    $onChanges({ filter }) {
        if (filter && filter.currentValue) {
            this.currentPage = 1
            this.filter = filter.currentValue

            if (this.filter.filialId) {
                this.loadingPromise = this.loadProdutos({})
                this.filterSubject.onNext(this.filter)
                this.subscribeProdutos()
            }
        }
    }

    $onDestroy() {
        this.unsubscribeProdutos()
        this.estatisticasSubject.onCompleted()

        this.etiquetasSubscription();
    }

    unsubscribeProdutos() {
        if (this.createdUnsubscriber) this.createdUnsubscriber()
        if (this.updatedUnsubscriber) this.updatedUnsubscriber()
    }

    subscribeProdutos() {
        this.unsubscribeProdutos()

        const variables = { filialId: this.filter.filialId }

        const createdSubscription = `
            subscription($filialId: String!) {
                produto: produtoCreated(filialId: $filialId) {
                    ${ProdutoList.produtoFields}
                }
            }
        `

        const updatedSubscription = `
            subscription($filialId: String!) {
                produto: produtoUpdated(filialId: $filialId) {
                    ${ProdutoList.produtoFields}
                }
            }
        `

        const debounce = (fn, timeout) => {
            let lastTimeout = 0
            return (...params) => {
                clearTimeout(lastTimeout)
                lastTimeout = setTimeout(() =>
                    fn(...params),
                    timeout
                )
            }
        }

        const onProdutoCreated = debounce(({ data: { produto } }) => {
            this.broadcast('hasNewRecords', produto)
        }, 500)

        const onProdutoUpdated = ({ data: { produto } }) => {
            const index = this.produtos.findIndex(p => p.id === produto.id)

            if (index >= 0) {
                this.produtos[index] = produto
            }

            this.estatisticasSubject.onNext()
            this.apply()
        }

        this.createdUnsubscriber = this.gql.subscribe(createdSubscription, variables, onProdutoCreated)
        this.updatedUnsubscriber = this.gql.subscribe(updatedSubscription, variables, onProdutoUpdated)
    }

    subscribeToEtiquetas(empresaId) {
        const etiquetaFields = `
            id
            produto { id }
            etiqueta {
                id
                nome
                descricao
                cor
            }
        `
        const produtoEtiquetasAdicionadas = `
            subscription($empresaId: String!) {
                etiquetas: produtoEtiquetasAdicionadas(empresaId: $empresaId) {
                    ${etiquetaFields}
                }
            }
        `

        const produtoEtiquetasRemovidas = `
            subscription($empresaId: String!) {
                etiquetas: produtoEtiquetasRemovidas(empresaId: $empresaId) {
                    ${etiquetaFields}
                }
            }
        `

        const onEtiquetasAdicionadas = ({ data: { etiquetas } }) => {
            const etiquetasByProdutoId = etiquetas.groupBy(e => e.produto.id, e => e.etiqueta)

            this.produtos
                .filter(produto => etiquetasByProdutoId[produto.id])
                .forEach(produto => {
                    const etiquetas = [...produto.etiquetas, ...etiquetasByProdutoId[produto.id]]
                        .uniqBy(e => e.id)

                    produto.etiquetas = etiquetas
                    produto.previousEtiquetas = etiquetas
                })

            this.apply()
        }

        const onEtiquetasRemovidas = ({ data: { etiquetas } }) => {
            const etiquetasIdsByProdutoId = etiquetas.groupBy(e => e.produto.id, e => e.etiqueta.id)

            this.produtos
                .filter(produto => etiquetasIdsByProdutoId[produto.id])
                .forEach(produto => {
                    const etiquetasToRemove = new Set(etiquetasIdsByProdutoId[produto.id])
                    const etiquetas = produto.etiquetas.reject(e => etiquetasToRemove.has(e.id))

                    produto.etiquetas = etiquetas
                    produto.previousEtiquetas = etiquetas
                })

            this.apply()
        }

        const adicionadasSubscriptions = this.gql
            .subscribe(produtoEtiquetasAdicionadas, { empresaId: empresaId.toString() }, onEtiquetasAdicionadas)

        const removidasSubscriptions = this.gql
            .subscribe(produtoEtiquetasRemovidas, { empresaId: empresaId.toString() }, onEtiquetasRemovidas)

        return () => {
            adicionadasSubscriptions()
            removidasSubscriptions()
        }
    }

    pageChange(page) {
        this.loadingPromise = this.loadProdutos(page)
    }

    loadProdutos(page) {
        const query = gql`
            query ProdutoList($filialId: Int!, $filter: ProdutoFilter, $order: ProdutoOrder, $after: String, $before: String) {
                filial(id: $filialId) {
                    id
                    produtosPage(filter: $filter, order: $order, after: $after, before: $before, size: ${this.perPage}) {
                        after
                        before
                        produtos: entries {
                            ... on Produto {
                                ${ProdutoList.produtoFields}
                            }
                        }
                    }
                    estatisticas {
                        reclassificacao {
                            produtosCount(filter: $filter)
                        }
                    }
                    ferramentasContratadas
                }
            }
        `

        const { filialId, order, ...filter } = this.filter

        const produtoFilterKeys = ['codigo', 'codigoBarras', 'descricao', 'ncm', 'exTipi', 'cest']

        this.currentTab = null

        if (produtoFilterKeys.some(key => filter.hasOwnProperty(key))) {
            filter.hasReclassificacao = {}
            this.currentTab = 'TODOS'
        }

        const variables = { filter, order, filialId: parseInt(filialId), ...page }

        return this.gql.query(query, variables)
            .then(({ filial: { produtosPage: { after, before, produtos }, estatisticas, ferramentasContratadas } }) => {
                this.apply(() => {
                    this.produtos = produtos.map(p => ({ ...p, previousEtiquetas: p.etiquetas }))
                    this.nextPage = after
                    this.previousPage = before
                    this.currentPage = this.currentPage + (page.after ? 1 : page.before ? -1 : 0)
                    this.produtosCount = estatisticas.reclassificacao.produtosCount
                    this.isConsultaTributacaoContratada = ferramentasContratadas.includes("consulta-simples-tributacao-cest")

                    if (!page.after && !page.before)
                        this.searchContext = new Date()
                })
            })
    }

    loadFoto(produto) {
        return this.Produtos.getFoto(parseInt(this.filter.filialId), produto.id, 'THUMB')
    }

    addFoto(produto, foto) {
        if (foto) {
            produto.imageContext = { isLoading: true }

            return this.Produtos
                .updateProdutoFoto(this.empresaId, produto.id, foto)
                .then(updated => {
                    const reader = new FileReader()

                    produto.foto = updated.foto

                    reader.onload = () => {
                        produto.imageContext = { image: reader.result, isLoading: false }
                        this.apply()
                    }

                    reader.readAsDataURL(foto)
                })
                .catch(console.error)
                .finally(() => this.apply())
        }
    }

    async editarProduto(produto) {
        this.modal
            .open({
                backdrop: 'static',
                size: 'md',
                component: 'produtoCadastroModal',
                resolve: {
                    filialId: () => this.filter.filialId,
                    empresaId: () => this.empresaId,
                    produto: () => produto
                }
            })
    }

    switchStatusProduto(produto) {
        Confirm.warning(
            'Atenção',
            `Deseja realmente ${produto.ativo ? 'desativar' : 'ativar'} o produto <b>${produto.codigo} - ${produto.descricao}</b>?`,
            {
                'Não': angular.noop,
                'Sim': () => {
                    const mutation = gql`
                        mutation UpdateProduto($empresaId: String!, $produtoId: String!, $ativo: Boolean!) {
                            empresa(id: $empresaId) {
                                produto: updateProduto(produtoId: $produtoId, produtoParams: {ativo: $ativo}) {
                                    id
                                    ativo
                                }
                            }
                        }
                    `

                    const variables = { empresaId: this.empresaId.toString(), produtoId: produto.id, ativo: !produto.ativo }

                    return this.gql.mutate(mutation, variables)
                        .then(({ empresa: { produto: { ativo } } }) => {

                            this.apply(() => {
                                this.produtos = this.produtos
                                    .map(p => {
                                        if (p.id == produto.id) {
                                            return { ...p, ativo }
                                        }

                                        return p
                                    })

                                if (typeof this.filter.ativo === "boolean") {
                                    this.produtos = this.produtos.filter(p =>
                                        p.ativo === this.filter.ativo
                                    )
                                }
                            })
                        })
                        .catch(console.error)
                }
            })
    }

    openCodigosFornecedoresModal(produto) {
        this.modal.open({
                backdrop: 'static',
                size: 'md',
                component: 'cadastroCodigoFornecedorModal',
                resolve: {
                    filialId: () => this.filter.filialId,
                    empresaId: () => this.empresaId,
                    produtoId: () => produto.id
                }
            })
            .result
            .then(codigos => {
                if (codigos) {
                    produto.codigosFornecedores = codigos
                }
            })
    }

    openConsultaTributariaModal(produto) {
        this.modal.open({
                backdrop: 'static',
                size: 'xl',
                component: 'consultaTributariaModal',
                resolve: {
                    ncm: () => produto.ncm,
                    contratoId: () => this.empresaId,
                    filialId: () => this.filter.filialId
                }
            })
    }

    countFornecedores(produto) {
        const grouped = produto.codigosFornecedores ? produto.codigosFornecedores.groupBy(c => c.cnpjFornecedor) : {}

        return Object.values(grouped).length
    }

    openReclassificacaoModal(produto) {
        this.modal
            .open({
                backdrop: 'static',
                size: 'lg',
                component: 'reclassificacaoFiscalModal',
                resolve: {
                    produtoId: () => produto.id,
                    filialId: () => this.filter.filialId
                }
            })
    }

    changeContextImage(produto) {
        produto.imageContext = { isLoading: true }

        this
            .loadFoto(produto)
            .then(fotoSource => this.loadImage(fotoSource))
            .then(image => produto.imageContext = { isLoading: false, image })
            .finally(() => this.apply())
    }

    loadImage(fotoSource) {
        return this.http
            .get(fotoSource, { responseType: "blob" })
            .then(({ data: blob }) => {
                return new Promise(resolve => {
                    const reader = new FileReader()

                    reader.onloadend = () => {
                        resolve(reader.result)
                    }

                    reader.readAsDataURL(blob)
                })
            })
    }

    setConclusaoFilter(conclusao) {
        const hasReclassificacao = conclusao ? { conclusao } : {}

        const filter = {
            filialId: this.filter.filialId,
            hasReclassificacao
        }

        this.emit('changeFilter', filter)
    }

    historicoProduto(produto) {
        this.modal
            .open({
                component: 'produtoHistoricoModal',
                size: 'lg',
                resolve: {
                    produtoId: () => produto.id,
                    filialId: () => parseInt(this.filter.filialId)
                }
            })
    }

    downloadXlsx() {
        this.modal.open({
            backdrop: 'static',
            component: 'produtosReportModal',
            size: 'lg',
            resolve: {
                filter: () => this.filter
            }
        })
    }

    hasAnyConclusao(produto, conclusoes) {
        return conclusoes.some(c => produto.reclassificacao.conclusoes.includes(c))
    }

    canExportProdutos() {
        return this.fieldsReprovados.some(field =>
            this.filter &&
            this.filter.hasReclassificacao.conclusao &&
            this.filter.hasReclassificacao.conclusao.includes(field)
        )
    }

    onEtiquetasClose(produto) {
        produto.etiquetasPromise = this.Etiquetas
            .setProdutoEtiquetas(this.empresaId, produto, produto.etiquetas)
            .then(etiquetas => {
                produto.etiquetas = etiquetas
                produto.previousEtiquetas = etiquetas
            })
            .catch(() => {
                produto.etiquetas = produto.previousEtiquetas
            })
            .finally(() => this.apply())

        produto.showEtiquetas = false
    }

    selectEtiqueta(etiqueta) {
        const filter = angular.copy(this.filter)

        filter.etiquetas = [etiqueta]

        this.emit('changeFilter', filter)
    }
}

angular.module("eficienciaFiscalModule")
    .component("produtoList", {
        controller: ProdutoList,
        bindings: {
            contratoId: '<',
            empresaId: '<',
            filter: '<'
        },
        template: `
            <div>
                <reclassificacao-fiscal-estatisticas 
                    changes-notifier="$ctrl.estatisticasObservable"
                    filter-notifier="$ctrl.filterSubject"
                    filial-id="$ctrl.filter.filialId"
                    current-tab="$ctrl.currentTab"
                    ng-model="$ctrl.reclassificacaoConclusao"
                    ng-change="$ctrl.setConclusaoFilter($ctrl.reclassificacaoConclusao)">
                </reclassificacao-fiscal-estatisticas>
                <loading promise="$ctrl.loadingPromise">
                    <highlighted ng-show="$ctrl.produtos.length == 0">
                        <main>Nenhuma informação encontrada.</main>
                    </highlighted>
                    <div ng-show="$ctrl.produtos.length > 0">
                        <div 
                            class="text-right" 
                            ng-class="{'push-down-exportacao-button': $ctrl.nextPage || $ctrl.previousPage}"
                            ng-if="$ctrl.canExportProdutos()">
                            <a 
                                class="btn btn-link" 
                                href="javascript:void(0)" 
                                ng-click="$ctrl.downloadXlsx()" 
                                style="padding-right: 0">
                                Exportar dados dos produtos <i class="fa fa-download"></i>
                            </a>
                        </div>

                        <cursor-paginator
                            per-page="$ctrl.perPage" next-page="$ctrl.nextPage" previous-page="$ctrl.previousPage"
                            current-page="$ctrl.currentPage" current-page-size="$ctrl.produtos.length" 
                            on-change="$ctrl.pageChange($page)" search-context="$ctrl.searchContext">
                        </cursor-paginator>

                        <table class="table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th class="text-center">Foto</th>
                                    <th>Código</th>
                                    <th>Descrição</th>
                                    <th>Código de barras</th>
                                    <th>NCM</th>
                                    <th>EX TIPI</th>
                                    <th>CEST</th>
                                    <th>Tipo</th>
                                    <th width="1">Opções</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr ng-repeat="produto in $ctrl.produtos">
                                    <td class="text-center-middle">
                                        {{ $index + 1 + (($ctrl.currentPage - 1) * $ctrl.perPage) }}
                                    </td>
                                    <td class="text-center text-middle">
                                        <a 
                                            href="javascript:void(0)"
                                            ng-click="$ctrl.currentImageContext = {isLoading: true}"
                                            popover-placement="top"
                                            popover-template="foto-produto" 
                                            popover-context="{
                                                produto: produto,
                                                changeContext: $ctrl.changeContextImage.bind($ctrl),
                                                addFoto: $ctrl.addFoto.bind($ctrl)
                                            }"
                                            tooltip="{{produto.foto ? 'Ver foto' : 'Adicionar foto'}}">
                                            <i class="fa {{produto.foto ? 'fa-picture-o' : 'fa-upload'}}" aria-hidden="true"></i>
                                        </a>
                                    </td>
                                    <td class="text-middle">
                                        {{ produto.codigo }}
                                    </td>

                                    <td class="text-middle">
                                        {{ produto.descricao }}
                                        <div style="position: relative">
                                            <ef-etiqueta 
                                                ng-repeat="etiqueta in produto.etiquetas | orderBy:'nome'"
                                                ng-click="$ctrl.selectEtiqueta(etiqueta)"
                                                style="margin: 0 5px 8px 0; display: inline-block; cursor: pointer;" 
                                                cor="{{etiqueta.cor}}" 
                                                nome="{{etiqueta.nome}}"
                                                descricao="{{etiqueta.descricao}}">
                                            </ef-etiqueta>
                                        </div>
                                    </td>
                                    <td class="text-middle">
                                        <span ng-show="produto.codigoBarras">{{ produto.codigoBarras }}</span>
                                        <span class="empty" ng-hide="produto.codigoBarras">VAZIO</span>
                                    </td>
                                    <td class="text-middle">
                                        <span ng-show="produto.ncm">{{ produto.ncm }}</span>
                                        <span class="empty" ng-hide="produto.ncm">VAZIO</span>
                                    </td>
                                    <td class="text-middle">
                                        <span ng-show="produto.exTipi">{{ produto.exTipi }}</span>
                                        <span class="empty" ng-hide="produto.exTipi">VAZIO</span>
                                    </td>

                                    <td class="text-middle">
                                        <span ng-show="produto.cest">{{ produto.cest | efMask:"00.000.00" }}</span>
                                        <span class="empty" ng-hide="produto.cest">VAZIO</span>                                        
                                    </td>

                                    <td class="text-middle">
                                        <span class="empty" ng-hide="produto.tipoDestinacao">VAZIO</span>
                                        <abbr ng-show="produto.tipoDestinacao" title="{{ produto.tipoDestinacao.descricao }}">
                                            {{ produto.tipoDestinacao.codigo }}
                                        </abbr>
                                    </td>
                                    
                                    <td class="text-middle no-break">
                                        <div class="btn-group">
                                            <a class="no-break" data-toggle="dropdown" href="javascript:void(0)">
                                                Operações <span class="caret"></span>
                                            </a>
                                            <ul class="dropdown-menu" style="right: 0; left: unset">
                                                <li>
                                                    <a href="javascript:void(0)" ng-click="$ctrl.switchStatusProduto(produto)">
                                                        {{ produto.ativo ? 'Desativar' : 'Ativar' }}
                                                    </a>
                                                </li>
                                                <li>
                                                    <a href="javascript:void(0)" ng-click="$ctrl.historicoProduto(produto)">
                                                        Histórico de alterações
                                                    </a>
                                                </li>
                                                <li>
                                                    <a href="javascript:void(0)" ng-click="$ctrl.openCodigosFornecedoresModal(produto)">
                                                        Fornecedores ({{ $ctrl.countFornecedores(produto) }})
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                        <br/>

                                        <a href="javascript:void(0)" ng-click="$ctrl.editarProduto(produto)">Alterar tipo de destinação</a>
                                        <br/>
                                        <span tooltip="{{ $ctrl.hasAnyConclusao(produto, ['NAO_RECLASSIFICADO', 'NCM_DISPENSA_CODIGO_BARRAS']) ? 'O produto não foi reclassificado.' : ''}}">
                                            <a 
                                                href="javascript:void(0)" 
                                                ng-click="$ctrl.openReclassificacaoModal(produto)" 
                                                ng-if="produto.reclassificacao"
                                                ng-disabled="$ctrl.hasAnyConclusao(produto, ['NAO_RECLASSIFICADO', 'NCM_DISPENSA_CODIGO_BARRAS'])">
                                                Reclassificação fiscal
                                                <i ng-show="$ctrl.hasAnyConclusao(produto, ['CLASSIFICACAO_INCORRETA', 'PARAMETROS_INCONSISTENTES', 'REPROVADO'])" class="fa fa-exclamation-triangle text-danger"></i>
                                                <i ng-show="$ctrl.hasAnyConclusao(produto, ['PENDENTE'])" class="fa fa-refresh text-info"></i>
                                                <i ng-show="$ctrl.hasAnyConclusao(produto, ['CLASSIFICACAO_CORRETA'])" class="fa fa-check text-success"></i>
                                            </a>
                                        </span>
                                        <br ng-if="$ctrl.isConsultaTributacaoContratada && produto.ncm && $ctrl.hasAnyConclusao(produto, ['CLASSIFICACAO_CORRETA'])"/>
                                        <span ng-if="$ctrl.isConsultaTributacaoContratada && produto.ncm && $ctrl.hasAnyConclusao(produto, ['CLASSIFICACAO_CORRETA'])">
                                            <a href="javascript:void(0)" ng-click="$ctrl.openConsultaTributariaModal(produto)">
                                                Consulta Tributária 
                                            </a>
                                        </span>
                                        <br/>
                                        <promise-tracker style="position: relative" promise="produto.etiquetasPromise" active="isActive">
                                            <span ng-show="isActive">
                                                Etiquetas
                                                <i class="fa fa-spinner fa-spin"></i>
                                            </span>
                                            
                                            <a ng-hide="isActive" href="javascript:void(0)" ng-click="produto.showEtiquetas = true">
                                                Etiquetas
                                            </a>

                                            <ef-etiquetas-popover 
                                                ng-if="produto.showEtiquetas" 
                                                ng-model="produto.etiquetas" 
                                                empresa-id="$ctrl.empresaId"
                                                on-close="$ctrl.onEtiquetasClose(produto)"
                                                position="end">
                                            </ef-etiquetas-popover>
                                        </promise-tracker>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <cursor-paginator
                            per-page="$ctrl.perPage" next-page="$ctrl.nextPage" previous-page="$ctrl.previousPage"
                            current-page="$ctrl.currentPage" current-page-size="$ctrl.produtos.length" 
                            on-change="$ctrl.pageChange($page)" search-context="$ctrl.searchContext">
                        </cursor-paginator>
                    </div>

                    <script type="text/ng-template" id="foto-produto">
                        <div
                            style="height: 100px; width: 100px;display: flex;align-items: center;justify-content: center;flex-direction: column;"
                            ng-init="$context.changeContext($context.produto)">
                            
                            <label class="upload" ng-if="$context.produto.imageContext.image && !$context.produto.imageContext.isLoading">
                                <div> 
                                    <img style="width: 100px;height: 100px;object-fit: cover;" ng-src="{{$context.produto.imageContext.image}}"/>
                                    <input ngf-select type="file" accept=".jpeg,.jpg,.png" ng-model="$context.foto" ng-change="$context.addFoto($context.produto, $context.foto)">
                                </div>
                                <div>{{ $context.produto.foto ? 'Trocar' : 'Adicionar' }} foto</div>
                            </label>
                            
                            <div class="text-center" ng-if="$context.produto.imageContext.isLoading"> 
                                <i class="fa fa-lg fa-spin fa-circle-o-notch" aria-hidden="true"></i> 
                                <br>
                                <span>Carregando...</span>
                            </div>
                        </div>
                    </script>
                </loading>
            </div>
        `
    })
